import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface Prenda {
  id: number;
  cliente_id: number;
  nombre: string;
  categoria: string;
  color: string;
  ocasion: string[];
  imagen_url: string | null;
  frecuencia_uso: number;
  estacion: string[];
  talla: string | null;
  material: string | null;
  notas: string | null;
  activa: boolean;
  sort_order: number;
}

const STORAGE_BUCKET = 'armario';

export function useClienteArmario(initialData: Prenda[], clienteId?: number) {
  const [data, setData] = useState<Prenda[]>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastClienteIdRef = useRef<number | undefined>(undefined);
  const isFirstMount = useRef(true);

  // On first mount or when clienteId changes: silently refetch in background
  // Keep existing data visible to avoid flash; update when response arrives
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      lastClienteIdRef.current = clienteId;
      setLoading(true);
      setData(initialData);
      return;
    }
    if (lastClienteIdRef.current !== clienteId) {
      lastClienteIdRef.current = clienteId;
      if (initialData.length > 0) {
        setData(initialData);
      }
      setError(null);
    }
  }, [initialData, clienteId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let query = supabase
          .from('cliente_armario')
          .select('*')
          .order('sort_order', { ascending: true });
        if (clienteId !== undefined && clienteId > 0) {
          query = query.eq('cliente_id', clienteId);
        }
        const { data: rows, error: err } = await query;
        if (err) {
          setError(err.message);
        } else if (rows && rows.length > 0) {
          setData(rows as Prenda[]);
        } else if (initialData.length > 0) {
          setData(initialData);
        }
      } catch {
        setError('Error de conexión. Usando datos locales.');
        if (initialData.length > 0) setData(initialData);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const add = useCallback(
    async (item: Omit<Prenda, 'id'>) => {
      setLoading(true);
      try {
        const { data: inserted, error: err } = await supabase
          .from('cliente_armario')
          .insert({
            cliente_id: item.cliente_id,
            nombre: item.nombre,
            categoria: item.categoria,
            color: item.color,
            ocasion: item.ocasion,
            imagen_url: item.imagen_url,
            frecuencia_uso: item.frecuencia_uso,
            estacion: item.estacion,
            talla: item.talla,
            material: item.material,
            notas: item.notas,
            activa: item.activa,
            sort_order: item.sort_order,
          })
          .select()
          .single();
        if (err) {
          setError(err.message);
          return;
        }
        if (inserted) {
          setData((prev) => [...prev, inserted as Prenda]);
        }
      } catch {
        setError('Error al crear');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const update = useCallback(async (id: number, updates: Partial<Prenda>) => {
    setLoading(true);
    try {
      const { data: updated, error: err } = await supabase
        .from('cliente_armario')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (err) {
        setError(err.message);
        return;
      }
      if (updated) {
        setData((prev) =>
          prev.map((item) => (item.id === id ? (updated as Prenda) : item))
        );
      }
    } catch {
      setError('Error al actualizar');
    } finally {
      setLoading(false);
    }
  }, []);

  const remove = useCallback(async (id: number) => {
    setLoading(true);
    try {
      const prenda = data.find((p) => p.id === id);
      if (prenda?.imagen_url && prenda.imagen_url.includes('/storage/v1/object/public/')) {
        try {
          const path = prenda.imagen_url.split('/storage/v1/object/public/' + STORAGE_BUCKET + '/')[1];
          if (path) await supabase.storage.from(STORAGE_BUCKET).remove([path]);
        } catch { /* ignore */ }
      }
      const { error: err } = await supabase
        .from('cliente_armario')
        .delete()
        .eq('id', id);
      if (err) {
        setError(err.message);
        return;
      }
      setData((prev) => prev.filter((item) => item.id !== id));
    } catch {
      setError('Error al eliminar');
    } finally {
      setLoading(false);
    }
  }, [data]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('cliente_armario')
        .select('*')
        .order('sort_order', { ascending: true });
      if (clienteId !== undefined && clienteId > 0) {
        query = query.eq('cliente_id', clienteId);
      }
      const { data: rows, error: err } = await query;
      if (err) {
        setError(err.message);
      } else if (rows) {
        setData(rows as Prenda[]);
      }
    } catch {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  const uploadImage = useCallback(async (file: File, _clienteId: number): Promise<string | null> => {
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${_clienteId}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from(STORAGE_BUCKET).upload(path, file, {
        cacheControl: '3600',
        upsert: false,
      });
      if (upErr) {
        setError('Error subiendo imagen: ' + upErr.message);
        return null;
      }
      const { data: urlData } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
      return urlData?.publicUrl ?? null;
    } catch {
      setError('Error subiendo imagen');
      return null;
    }
  }, []);

  return { data, setData, add, update, remove, refresh, uploadImage, loading, error };
}
